/**
 * Gallery Service - Handles photo album and photo management operations
 * Provides CRUD operations for photo albums and photos with image upload support
 */

import { safeFrom, safeStorage, safeAuth, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notFoundError,
  notConfiguredError
} from '@/lib/service-errors';
import { validateFileUpload } from '@/lib/input-validation';
import { optimizeImage, IMAGE_PRESETS } from '@/lib/image-optimizer';
import { auditContent } from '@/lib/audit-logger';
import type { 
  PhotoAlbum, 
  Photo, 
  PhotoAlbumFormValues, 
  PhotoFormValues,
  ImageUploadResult 
} from '@/types/gallery';

export const galleryService = {
  // Photo Album Operations
  
  /**
   * Get all photo albums (published only for public, all for admins)
   * @param isAdmin - Whether the request is from an admin user
   * @returns Array of photo albums with photo counts
   */
  async getPhotoAlbums({ isAdmin = false }: { isAdmin?: boolean } = {}): Promise<ServiceResponse<PhotoAlbum[]>> {
    if (!isSupabaseAvailable()) {
      return successResponse([]);
    }

    try {
      let query = safeFrom('photo_albums')
        .select(`
          *,
          photos:photos(count)
        `)
        .order('created_at', { ascending: false });

      if (!isAdmin) {
        query = query.eq('is_published', true) as any;
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Transform the data to include photo_count and map fields
      const albums = (data || []).map((album: any) => ({
        ...album,
        photo_count: album.photos?.[0]?.count || 0,
        cover_image: album.cover_image ?? album.cover_image_url ?? undefined,
        published: album.published ?? album.is_published ?? true,
        photos: undefined
      }));

      return successResponse(albums);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'galleryService.getPhotoAlbums'));
    }
  },

  /**
   * Get a single photo album by ID with its photos
   * @param id - Album ID
   * @param isAdmin - Whether the request is from an admin user
   * @returns Photo album with photos
   */
  async getPhotoAlbum(id: number, { isAdmin = false }: { isAdmin?: boolean } = {}): Promise<ServiceResponse<PhotoAlbum | null>> {
    if (!isSupabaseAvailable()) {
      return successResponse(null);
    }

    try {
      const query = safeFrom('photo_albums')
        .select(`
          *,
          photos:photos(*)
        `)
        .eq('id', id)
        .maybeSingle();

      const { data, error } = await query;

      if (error) {
        if (error.code === 'PGRST116') {
          return successResponse(null);
        }
        throw error;
      }

      if (!data) {
        return successResponse(null);
      }

      // Map album fields to expected UI shape
      const mapped: any = {
        ...data,
        published: (data as any).published ?? (data as any).is_published ?? true,
        cover_image: (data as any).cover_image ?? (data as any).cover_image_url ?? undefined,
      };

      // Map photos to expected UI shape and sort by order_index (or sort_order)
      if (mapped.photos) {
        mapped.photos = (mapped.photos as any[]).map((p: any) => ({
          ...p,
          url: p.url ?? p.image_url,
          caption: p.caption ?? p.title ?? '',
          order_index: p.order_index ?? p.sort_order ?? 0,
          published: p.published ?? true,
        })).sort((a: any, b: any) => a.order_index - b.order_index);
      }

      return successResponse(mapped as PhotoAlbum);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'galleryService.getPhotoAlbum', { id }));
    }
  },

  /**
   * Create a new photo album
   * @param albumData - Album data to create
   * @returns Created photo album
   */
  async createPhotoAlbum(albumData: PhotoAlbumFormValues): Promise<ServiceResponse<PhotoAlbum>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const payload: any = {
        title: albumData.title,
        description: albumData.description ?? null,
        cover_image_url: (albumData as any).cover_image ?? null,
        is_published: albumData.published ?? true,
      };

      const { data, error } = await safeFrom('photo_albums')
        .insert([payload])
        .select()
        .single();

      if (error) {
        throw error;
      }

      const album = data as PhotoAlbum;
      
      // Audit log
      await auditContent.create('gallery', album.id.toString(), {
        title: album.title,
      });

      return successResponse(album);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'galleryService.createPhotoAlbum'));
    }
  },

  /**
   * Update an existing photo album
   * @param id - Album ID
   * @param albumData - Updated album data
   * @returns Updated photo album
   */
  async updatePhotoAlbum(id: number, albumData: Partial<PhotoAlbumFormValues>): Promise<ServiceResponse<PhotoAlbum>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const payload: any = {};
      if (albumData.title !== undefined) payload.title = albumData.title;
      if (albumData.description !== undefined) payload.description = albumData.description;
      if ((albumData as any).cover_image !== undefined) payload.cover_image_url = (albumData as any).cover_image;
      if (albumData.published !== undefined) payload.is_published = albumData.published;

      const { data, error } = await safeFrom('photo_albums')
        .update(payload)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const album = data as PhotoAlbum;
      
      // Audit log
      await auditContent.update('gallery', id.toString(), {}, {
        title: album.title,
        updated_at: album.updated_at || new Date().toISOString(),
      });

      return successResponse(album);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'galleryService.updatePhotoAlbum', { id }));
    }
  },

  /**
   * Delete a photo album and all its photos
   * @param id - Album ID to delete
   * @returns Success response
   */
  async deletePhotoAlbum(id: number): Promise<ServiceResponse<boolean>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { error } = await safeFrom('photo_albums')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      // Audit log
      await auditContent.delete('gallery', id.toString());

      return successResponse(true);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'galleryService.deletePhotoAlbum', { id }));
    }
  },

  // Photo Operations

  /**
   * Get photos for a specific album
   * @param albumId - Album ID
   * @param isAdmin - Whether the request is from an admin user
   * @returns Array of photos
   */
  async getPhotos(albumId: number, { isAdmin = false }: { isAdmin?: boolean } = {}): Promise<ServiceResponse<Photo[]>> {
    if (!isSupabaseAvailable()) {
      return successResponse([]);
    }

    try {
      const query = safeFrom('photos')
        .select('*')
        .eq('album_id', albumId)
        .order('sort_order', { ascending: true });

      if (!isAdmin) {
        // For public, only show published photos (if published field exists)
        // Note: The schema might not have a published field on photos
        // This is a placeholder for future enhancement
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Map DB fields to expected UI shape
      const photos = (data || []).map((p: any) => ({
        ...p,
        url: p.url ?? p.image_url,
        caption: p.caption ?? p.title ?? '',
        order_index: p.order_index ?? p.sort_order ?? 0,
        published: p.published ?? true,
      }));

      return successResponse(photos);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'galleryService.getPhotos', { albumId }));
    }
  },

  /**
   * Upload a single photo to an album
   * @param albumId - Album ID
   * @param file - Image file to upload
   * @param photoData - Additional photo metadata
   * @returns Created photo record
   */
  async uploadPhoto(albumId: number, file: File, photoData: Partial<PhotoFormValues> = {}): Promise<ServiceResponse<Photo>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      // Upload file to storage (includes validation and optimization)
      const uploadResult = await this.uploadImage(file, `albums/${albumId}`);
      if (!uploadResult.success) {
        return uploadResult as any;
      }
    
      // Create photo record
      const { data, error } = await safeFrom('photos')
        .insert([{
          album_id: albumId,
          image_url: uploadResult.data.url,
          title: photoData.caption ?? null,
          description: photoData.description ?? null,
          sort_order: photoData.order_index || 0
        }])
        .select()
        .single();

      if (error) {
        throw error;
      }

      const photo = data as Photo;
      
      // Audit log
      await auditContent.create('gallery', photo.id.toString(), {
        album_id: albumId,
        title: photo.title,
      });

      return successResponse(photo);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'galleryService.uploadPhoto', { albumId }));
    }
  },

  /**
   * Upload multiple photos to an album
   * @param albumId - Album ID
   * @param files - Array of image files
   * @param progressCallback - Optional progress callback
   * @returns Array of created photo records
   */
  async uploadPhotos(
    albumId: number, 
    files: File[], 
    progressCallback?: (progress: { completed: number; total: number; current?: string }) => void
  ): Promise<ServiceResponse<Photo[]>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    const results: Photo[] = [];
    let completed = 0;

    for (const file of files) {
      try {
        // Validate file before processing
        if (!file || !(file instanceof File) || !file.name || file.size === undefined) {
          console.error('Invalid file object:', file);
          continue;
        }
        
        progressCallback?.({ completed, total: files.length, current: file.name });
        
        const result = await this.uploadPhoto(albumId, file);
        if (result.success && result.data) {
          results.push(result.data);
          completed++;
        } else {
          console.error(`Error uploading ${file.name || 'unknown'}:`, result.error);
          // Continue with other files
        }
        
        progressCallback?.({ completed, total: files.length });
      } catch (error) {
        console.error(`Error uploading ${file?.name || 'unknown'}:`, error);
        // Continue with other files
      }
    }

    return successResponse(results);
  },

  /**
   * Update photo metadata
   * @param photoId - Photo ID
   * @param photoData - Updated photo data
   * @returns Updated photo
   */
  async updatePhoto(photoId: number, photoData: Partial<PhotoFormValues>): Promise<ServiceResponse<Photo>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const payload: any = {};
      if (photoData.caption !== undefined) payload.title = photoData.caption;
      if (photoData.description !== undefined) payload.description = photoData.description;
      if (photoData.order_index !== undefined) payload.sort_order = photoData.order_index;

      const { data, error } = await safeFrom('photos')
        .update(payload)
        .eq('id', photoId)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const photo = data as Photo;

      // Audit log
      await auditContent.update('gallery', photoId.toString(), {}, {
        album_id: photo.album_id,
        title: photo.caption,
      });

      return successResponse(photo);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'galleryService.updatePhoto', { photoId }));
    }
  },

  /**
   * Delete a photo
   * @param photoId - Photo ID to delete
   * @returns Success response
   */
  async deletePhoto(photoId: number): Promise<ServiceResponse<boolean>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      // Get photo info first to delete from storage
      const { data: photo } = await safeFrom('photos')
        .select('image_url, url')
        .eq('id', photoId)
        .maybeSingle();

      // Delete from database
      const { error } = await safeFrom('photos')
        .delete()
        .eq('id', photoId);

      if (error) {
        throw error;
      }

      // Delete from storage if we have the URL
      const imageUrl = photo?.url || photo?.image_url;
      if (imageUrl) {
        await this.deleteImageFromStorage(imageUrl);
      }

      // Audit log
      await auditContent.delete('gallery', photoId.toString());

      return successResponse(true);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'galleryService.deletePhoto', { photoId }));
    }
  },

  /**
   * Reorder photos in an album
   * @param photoUpdates - Array of {id, order_index} objects
   * @returns Success response
   */
  async reorderPhotos(photoUpdates: { id: number; order_index: number }[]): Promise<ServiceResponse<boolean>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      // Update each photo's sort_order (using sort_order as that's the DB field)
      const updates = photoUpdates.map(({ id, order_index }) =>
        safeFrom('photos')
          .update({ sort_order: order_index })
          .eq('id', id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter(r => r.error);

      if (errors.length > 0) {
        console.error('Error reordering photos:', errors);
        throw new Error('Failed to reorder some photos');
      }

      return successResponse(true);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'galleryService.reorderPhotos'));
    }
  },

  // Image Upload Utilities

  /**
   * Upload an image file to Supabase storage
   * @param file - Image file
   * @param path - Storage path (without filename)
   * @returns Upload result with public URL
   */
  async uploadImage(file: File, path: string = 'gallery'): Promise<ServiceResponse<ImageUploadResult>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase Storage'));
    }

    // Validate file object first
    if (!file || !(file instanceof File)) {
      return errorResponse(handleServiceError(
        new Error('Invalid file object'),
        'galleryService.uploadImage',
        { path }
      ));
    }

    // Validate file has required properties
    if (!file.name || typeof file.name !== 'string' || file.size === undefined) {
      return errorResponse(handleServiceError(
        new Error('Invalid file: missing filename or size'),
        'galleryService.uploadImage',
        { fileName: file.name || 'unknown', path }
      ));
    }

    // Validate file upload
    const validation = validateFileUpload(file);
    if (!validation.valid) {
      return errorResponse(handleServiceError(
        new Error(validation.error || 'Invalid file'),
        'galleryService.uploadImage',
        { fileName: file.name, path }
      ));
    }

    try {
      // Optimize image before upload
      const optimizedFile = await optimizeImage(file, IMAGE_PRESETS.gallery);

      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const fileName = `${path}/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const storage = safeStorage.from('gallery-images');
      const { data: uploadData, error: uploadError } = await storage.upload(fileName, optimizedFile, {
        cacheControl: '3600',
        upsert: false
      });

      if (uploadError) {
        throw uploadError;
      }

      // Get the public URL
      const { data: { publicUrl } } = storage.getPublicUrl(fileName);

      const result: ImageUploadResult = {
        url: publicUrl,
        filename: fileName,
        size: optimizedFile.size,
        type: file.type
      };

      return successResponse(result);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'galleryService.uploadImage', { path }));
    }
  },

  /**
   * Delete an image from storage
   * @param imageUrl - Public URL of the image
   */
  async deleteImageFromStorage(imageUrl: string): Promise<void> {
    if (!isSupabaseAvailable()) {
      return;
    }

    try {
      // Extract filename from URL
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      
      if (!fileName) return;

      const storage = safeStorage.from('gallery-images');
      const { error } = await storage.remove([fileName]);

      if (error) {
        console.error('Error deleting image from storage:', error);
      }
    } catch (error) {
      console.error('Error parsing image URL for deletion:', error);
    }
  },

  /**
   * Validate image file
   * @param file - File to validate
   * @returns Validation result
   */
  validateImageFile(file: File): { valid: boolean; error?: string } {
    // Use centralized validation
    return validateFileUpload(file, [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/gif'
    ], 10 * 1024 * 1024); // 10MB
  }
};

